import {
  BarChart3,
  BookOpen,
  Building2,
  CalendarClock,
  CalendarDays,
  ChevronRight,
  FileCheck,
  FileDown,
  FileSearch,
  FileText,
  FolderOpen,
  Handshake,
  Heart,
  Inbox,
  Landmark,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  Package,
  Plus,
  Receipt,
  ScrollText,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
  SlidersHorizontal,
  Stamp,
  Store,
  Truck,
  UserCircle,
  Users,
  MessagesSquare,
  MoreHorizontal,
  MessageCircle,} from "lucide-react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { getTenantAccountantRequests } from "@/services/fiscalService";
import {
  getMyAccountantPulse,
  getMyAccountantRequests,
} from "@/services/authService";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useSidebar } from "@/contexts/SidebarContext";
import { useDashboardView } from "@/hooks/useDashboardView";
import { NuevaFacturaDialog } from "@/pages/invoices/NuevaFacturaDialog";
import { FeedbackDialog } from "@/components/shared/FeedbackDialog";
import { useTenantSwitcher } from "@/contexts/TenantSwitcherContext";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { TenantContextBar } from "@/components/shared/TenantContextBar";
import { ThemePreferenceSubmenu } from "@/components/shared/ThemeToggle";

function getDisplayName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  displayName: string | null | undefined,
  email: string
): string {
  const parts = [firstName, lastName].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  if (displayName?.trim()) return displayName;
  return email;
}

function getInitials(
  email?: string | null,
  firstName?: string | null,
  lastName?: string | null
): string {
  if (!email) return "?";
  if (firstName && lastName) return `${firstName[0]}${lastName[0]}`.toUpperCase();
  if (firstName) return firstName.slice(0, 2).toUpperCase();
  return email.slice(0, 2).toUpperCase();
}

const navItemBase =
  "fx-nav-item flex items-center gap-3 transition-colors";
const navItemCollapsed =
  "lg:!justify-center lg:!h-8 lg:!w-8 lg:!p-0 lg:!mx-auto lg:!gap-0";
const navSubItemBase = "fx-nav-subitem flex items-center gap-2.5";

/**
 * Wraps the full tenant nav. In advanced mode it renders its children inline,
 * exactly as before. In simple mode it hides them behind a "Más" disclosure —
 * mirroring mobile, whose fourth tab is a "Más" catch-all (TabBar.tsx:42).
 *
 * Collapsing rather than dropping items is deliberate: simple mode must not
 * strand Clientes, Proveedores, Gastos, Reportes or the DGI group, several of
 * which have no other entry point.
 */
function SidebarMoreGroup({
  collapsed,
  label,
  children,
}: {
  collapsed: boolean;
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  if (!collapsed) return <>{children}</>;
  return (
    <Collapsible className="group/more">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className={cn(
            navItemBase,
            "w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fx-accent-300)] focus-visible:ring-offset-0"
          )}
        >
          <span className="fx-nav-icon">
            <MoreHorizontal className="h-4 w-4" />
          </span>
          <span className="flex-1 text-left">{label}</span>
          <ChevronRight className="fx-nav-chev h-3 w-3 transition-transform duration-200 group-data-[state=open]/more:rotate-90" />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-1 space-y-0.5">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function Sidebar(): JSX.Element {
  const { t } = useTranslation();
  const { view } = useDashboardView();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { user, logout } = useAuth();
  const { activeTenant } = useTenantSwitcher();
  const { isSidebarOpen, closeSidebar, toggleSidebar } = useSidebar();
  const navigate = useNavigate();
  const isPlatform =
    user?.platformRole === "root" || user?.platformRole === "root_admin";
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);

  const handleLogout = (): void => {
    setIsLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = async (): Promise<void> => {
    await logout();
    setIsLogoutDialogOpen(false);
    navigate("/login", { replace: true });
  };

  const displayName = getDisplayName(
    user?.firstName,
    user?.lastName,
    user?.displayName,
    user?.email ?? ""
  );

  const canAccessUsers =
    isPlatform ||
    user?.role === "owner" ||
    user?.role === "admin" ||
    user?.role === "accountant";

  const canAccessClients =
    isPlatform ||
    ["owner", "admin", "member", "accountant", "read_only"].includes(
      user?.role ?? ""
    );

  const isAccountant =
    user?.accountType === "accountant" || user?.role === "accountant";

  const location = useLocation();
  const isInvoicesSection = location.pathname === "/invoices" || location.pathname.startsWith("/invoices/");
  const isContabilidadSection =
    location.pathname === "/fiscal-status" ||
    location.pathname.startsWith("/fiscal-status/") ||
    location.pathname === "/reports" ||
    location.pathname.startsWith("/reports/") ||
    location.pathname === "/expenses" ||
    location.pathname.startsWith("/expenses/");
  const isDgiSection =
    location.pathname === "/dgi-documents" ||
    location.pathname.startsWith("/dgi-documents/") ||
    location.pathname === "/dgi/daily-reports" ||
    location.pathname.startsWith("/dgi/daily-reports/");

  const isImpersonating = Boolean(isPlatform && activeTenant);
  const onAccountantHome = location.pathname.startsWith("/accountant");
  // The tenant-scoped sidebar (Panel, Facturas, Contabilidad, etc.) only
  // makes sense when the user is "inside" a tenant. Hide it for platform
  // admins without an active tenant AND for accountants viewing their
  // portfolio at /accountant — they aren't acting on any single client there.
  const showTenantSidebar =
    !(isPlatform && !activeTenant) && !onAccountantHome;

  // Accountants get a cross-tenant badge: total pending+in-progress requests
  // across every client they belong to. Other roles see the per-tenant count.
  const { data: aggregateRequests } = useQuery({
    queryKey: ["sidebar-aggregate-requests"],
    queryFn: () => getMyAccountantRequests(["pending", "in_progress"]),
    enabled: isAccountant && onAccountantHome,
    refetchInterval: 60_000,
  });
  const { data: pendingRequests } = useQuery({
    queryKey: ["sidebar-pending-requests"],
    queryFn: () => getTenantAccountantRequests(["pending", "in_progress"]),
    enabled: !isAccountant && showTenantSidebar,
    refetchInterval: 60_000,
  });
  const pendingCount = isAccountant
    ? (aggregateRequests?.length ?? 0)
    : (pendingRequests?.length ?? 0);

  // Total clients in the accountant's portfolio — drives the count pill
  // next to the "Clientes" nav item. Same React Query cache as the page
  // itself, so it's free once the user lands on /accountant.
  const { data: accountantPulse } = useQuery({
    queryKey: ["accountant", user?.id ?? null, "pulse"],
    queryFn: getMyAccountantPulse,
    enabled: isAccountant && Boolean(user?.id),
    refetchInterval: 60_000,
  });
  const clientsCount = accountantPulse?.totalClients ?? 0;

  // Simple mode applies whenever the ordinary TENANT nav is on screen and the
  // sidebar is expanded.
  //
  // Deliberately NOT gated on !isPlatform: platformRole is a property of the
  // user, not of the nav being rendered, so that clause suppressed simple mode
  // for a platform user working inside a tenant — i.e. for the normal case on
  // this team. showTenantSidebar already excludes the platform ADMIN nav,
  // since it is false when isPlatform && !activeTenant.
  //
  // Accountants keep the full nav: their Bandeja/Solicitudes/Clientes items
  // render above the collapsed region and are a different audience. The
  // collapsed icon-rail also keeps its behaviour — there is nowhere to hang a
  // "Más" disclosure.
  const simpleNav =
    view === "simple" && showTenantSidebar && isSidebarOpen && !isAccountant;

  const navigationItems = useMemo(() => {
    // Clientes + Proveedores intentionally NOT in this array — they
    // render as standalone NavLinks immediately under Contabilidad so
    // the user reaches their cartera without scrolling past DGI + Agenda.
    const tenantNav = [
      { to: "/documents", labelKey: "navigation.documents", icon: FolderOpen },
      ...(canAccessUsers && (!isPlatform || isImpersonating)
        ? [{ to: "/users", labelKey: "navigation.users", icon: Users }]
        : []),
      { to: "/settings", labelKey: "navigation.settings", icon: Settings },
    ];
    if (isPlatform && !activeTenant) {
      return [
        { to: "/tenants", labelKey: "navigation.tenants", icon: Store },
        // Support sits high on purpose: it is the only item here that is
        // time-sensitive — a waiting customer gets more annoyed by the
        // minute, unlike a metrics page or a config screen.
        {
          to: "/platform-support",
          labelKey: "navigation.platformSupport",
          icon: MessagesSquare,
        },
        // Time-sensitive like Support: a self-onboarded emisor is parked on the
        // "homologación pendiente" screen and cannot invoice until the team
        // finishes their homologación, so this queue sits high.
        {
          to: "/homologaciones",
          labelKey: "navigation.homologaciones",
          icon: Stamp,
        },
        { to: "/platform-metrics", labelKey: "navigation.platformMetrics", icon: BarChart3 },
        // The route existed and the page was complete, but nothing linked to it —
        // it was reachable only by typing the URL. This is where the Form 2181
        // PADI file is downloaded each month, so it needs to be findable.
        {
          to: "/platform-fiscal-benefit",
          labelKey: "navigation.platformFiscalBenefit",
          icon: FileDown,
        },
        { to: "/bank-transfer-reviews", labelKey: "navigation.bankTransferReviews", icon: Landmark },
        { to: "/partners", labelKey: "navigation.partners", icon: Handshake },
        { to: "/certificate-requests", labelKey: "navigation.certificateRequests", icon: FileCheck },
        { to: "/users", labelKey: "navigation.users", icon: Users },
        { to: "/proveedor-software", labelKey: "navigation.proveedorSoftware", icon: Package },
        { to: "/platform-config", labelKey: "navigation.platformConfig", icon: SlidersHorizontal },
        { to: "/feedback", labelKey: "navigation.feedback", icon: MessageSquare },
      ];
    }
    if (isPlatform && activeTenant) {
      return tenantNav;
    }
    return tenantNav;
  }, [isPlatform, activeTenant, canAccessUsers, canAccessClients]);

  return (
    <>
      {isSidebarOpen && (
        <button
          type="button"
          className="fixed inset-x-0 top-16 bottom-0 z-30 bg-background/60 backdrop-blur lg:hidden"
          onClick={closeSidebar}
          aria-label="Close sidebar"
        />
      )}
      <aside
        className={cn(
          "fx-sidebar fixed inset-y-0 left-0 z-40 flex flex-col shadow-lg transition-all duration-300 lg:static lg:h-full lg:shadow-none",
          "px-4 pt-5 pb-4",
          isSidebarOpen ? "translate-x-0" : "-translate-x-full",
          "lg:translate-x-0",
          isSidebarOpen ? "w-64" : "lg:w-16 fx-sidebar--collapsed"
        )}
      >
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-0 top-4 z-50 hidden h-8 w-8 translate-x-1/2 rounded-full border border-[var(--fx-ink-200)] bg-[var(--fx-paper)] text-[var(--fx-ink-700)] shadow-md transition-all hover:bg-[var(--fx-s-raised-hover)] hover:text-[var(--fx-ink-900)] dark:border-[var(--dk-border-2)] dark:bg-[var(--dk-surface)] dark:text-[var(--dk-text)] dark:hover:bg-[var(--fx-s-raised-hover)] dark:hover:text-[var(--dk-text)] lg:flex"
          onClick={toggleSidebar}
          aria-label={t("header.toggleNavigation")}
        >
          {isSidebarOpen ? (
            <PanelLeftClose className="h-4 w-4" />
          ) : (
            <PanelLeftOpen className="h-4 w-4" />
          )}
        </Button>
        <div
          className={cn(
            "hidden items-center lg:flex",
            isSidebarOpen ? "fx-sidebar-brand" : "justify-center pb-4 pt-2"
          )}
        >
          <NavLink to="/" className="flex items-center">
            {isSidebarOpen ? (
              <img
                src="/brand/fiscalo-logo-white.svg"
                alt="Fiscalo"
                className="h-8 shrink-0"
              />
            ) : (
              <img
                src="/brand/fiscalo-iso-white.svg"
                alt="Fiscalo"
                className="h-7 w-7 shrink-0"
              />
            )}
          </NavLink>
        </div>
        {activeTenant ? (
          <div className="border-b border-[rgba(244,239,230,0.08)] pb-3 mb-1 lg:hidden">
            <TenantContextBar className="w-full" />
          </div>
        ) : null}
        <nav className="flex-1 space-y-0.5 overflow-x-hidden overflow-y-auto pt-2 pr-1">
          {isAccountant && onAccountantHome ? (
            <NavLink
              to="/accountant"
              end
              onClick={() => {
                if (window.innerWidth < 1024) closeSidebar();
              }}
              className={({ isActive }) =>
                cn(navItemBase, isActive && "active", !isSidebarOpen && navItemCollapsed)
              }
              title={
                !isSidebarOpen
                  ? t("accountant.sidebar.label", "Clientes")
                  : undefined
              }
            >
              <span className="fx-nav-icon">
                <Building2 className="h-4 w-4" />
              </span>
              {isSidebarOpen && (
                <span className="flex flex-1 items-center justify-between">
                  <span>{t("accountant.sidebar.label", "Clientes")}</span>
                  {clientsCount > 0 ? (
                    <span className="fx-nav-count">{clientsCount}</span>
                  ) : null}
                </span>
              )}
            </NavLink>
          ) : null}

          {showTenantSidebar && (
            <NavLink
              to="/dashboard"
              onClick={() => {
                if (window.innerWidth < 1024) closeSidebar();
              }}
              className={({ isActive }) =>
                cn(navItemBase, isActive && "active", !isSidebarOpen && navItemCollapsed)
              }
              title={!isSidebarOpen ? t("navigation.dashboard") : undefined}
            >
              <span className="fx-nav-icon">
                <LayoutDashboard className="h-4 w-4" />
              </span>
              {isSidebarOpen && <span className="flex-1">{t("navigation.dashboard")}</span>}
            </NavLink>
          )}

          {isAccountant && onAccountantHome ? (
            <NavLink
              to="/accountant/inbox"
              end={false}
              onClick={() => {
                if (window.innerWidth < 1024) closeSidebar();
              }}
              className={({ isActive }) =>
                cn(
                  navItemBase,
                  "relative",
                  isActive && "active",
                  !isSidebarOpen && navItemCollapsed,
                )
              }
              title={!isSidebarOpen ? t("navigation.bandeja", "Bandeja") : undefined}
            >
              <span className="fx-nav-icon">
                <Inbox className="h-4 w-4" />
              </span>
              {isSidebarOpen && (
                <span className="flex flex-1 items-center justify-between">
                  <span>{t("navigation.bandeja", "Bandeja")}</span>
                </span>
              )}
            </NavLink>
          ) : null}

          {isAccountant && onAccountantHome ? (
            <NavLink
              to="/accountant/requests"
              end={false}
              onClick={() => {
                if (window.innerWidth < 1024) closeSidebar();
              }}
              className={({ isActive }) =>
                cn(navItemBase, "relative", isActive && "active", !isSidebarOpen && navItemCollapsed)
              }
              title={!isSidebarOpen ? t("navigation.solicitudes") : undefined}
            >
              <span className="fx-nav-icon">
                <FileCheck className="h-4 w-4" />
              </span>
              {isSidebarOpen && (
                <span className="flex flex-1 items-center justify-between">
                  <span>{t("navigation.solicitudes")}</span>
                  {pendingCount > 0 ? (
                    <span className="fx-nav-count warn">{pendingCount}</span>
                  ) : null}
                </span>
              )}
              {!isSidebarOpen && pendingCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5 rounded-full bg-[var(--fx-warn)]" />
              )}
            </NavLink>
          ) : null}

          {/* Simple mode mirrors the mobile tab bar: Nueva factura (the Emitir
              FAB), Facturas, Estado. Inicio is the /dashboard link above.
              Everything else moves inside the "Más" group below — mobile's
              fourth slot is literally "Más" (TabBar.tsx:42), a catch-all. */}
          {simpleNav ? (
            <>
              <button
                type="button"
                onClick={() => {
                  setPickerOpen(true);
                  if (window.innerWidth < 1024) closeSidebar();
                }}
                className={cn(navItemBase, "w-full text-left")}
              >
                <span className="fx-nav-icon">
                  <Plus className="h-4 w-4" />
                </span>
                <span className="flex-1">{t("navigation.newInvoice")}</span>
              </button>
              <NavLink
                to="/invoices"
                end
                onClick={() => {
                  if (window.innerWidth < 1024) closeSidebar();
                }}
                className={({ isActive }) => cn(navItemBase, isActive && "active")}
              >
                <span className="fx-nav-icon">
                  <FileText className="h-4 w-4" />
                </span>
                <span className="flex-1">{t("navigation.emitidas")}</span>
              </NavLink>
              <NavLink
                to="/fiscal-status"
                onClick={() => {
                  if (window.innerWidth < 1024) closeSidebar();
                }}
                className={({ isActive }) => cn(navItemBase, isActive && "active")}
              >
                <span className="fx-nav-icon">
                  {/* Same icon mobile uses for this tab (TabBar.tsx:36). */}
                  <Heart className="h-4 w-4" />
                </span>
                <span className="flex-1">
                  {t("navigation.estado", "Estado")}
                </span>
              </NavLink>
            </>
          ) : null}

          <SidebarMoreGroup
            collapsed={simpleNav}
            label={t("navigation.more", "Más")}
          >
          {showTenantSidebar ? (
            isSidebarOpen ? (
              <Collapsible
                defaultOpen={isInvoicesSection}
                className="group/collapsible"
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      navItemBase,
                      "w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fx-accent-300)] focus-visible:ring-offset-0",
                      isInvoicesSection && "active"
                    )}
                  >
                    <span className="fx-nav-icon">
                      <FileText className="h-4 w-4" />
                    </span>
                    <span className="flex-1 text-left">
                      {t("navigation.invoices")}
                    </span>
                    <ChevronRight className="fx-nav-chev h-3 w-3 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="mt-1 space-y-0.5 pl-7">
                    <NavLink
                      to="/invoices/new"
                      onClick={() => {
                        if (window.innerWidth < 1024) closeSidebar();
                      }}
                      className={({ isActive }) =>
                        cn(navSubItemBase, isActive && "active")
                      }
                    >
                      <span className="fx-nav-icon">
                        <Plus className="h-3.5 w-3.5" />
                      </span>
                      <span>{t("navigation.newInvoice")}</span>
                    </NavLink>
                    <NavLink
                      to="/invoices"
                      end
                      onClick={() => {
                        if (window.innerWidth < 1024) closeSidebar();
                      }}
                      className={({ isActive }) =>
                        cn(navSubItemBase, isActive && "active")
                      }
                    >
                      <span className="fx-nav-icon">
                        <FileText className="h-3.5 w-3.5" />
                      </span>
                      <span>{t("navigation.emitidas")}</span>
                    </NavLink>
                    <NavLink
                      to="/invoices/recibidas"
                      onClick={() => {
                        if (window.innerWidth < 1024) closeSidebar();
                      }}
                      className={({ isActive }) =>
                        cn(navSubItemBase, isActive && "active")
                      }
                    >
                      <span className="fx-nav-icon">
                        <FileDown className="h-3.5 w-3.5" />
                      </span>
                      <span>{t("navigation.recibidas")}</span>
                    </NavLink>
                    <NavLink
                      to="/invoices/recurring"
                      onClick={() => {
                        if (window.innerWidth < 1024) closeSidebar();
                      }}
                      className={({ isActive }) =>
                        cn(navSubItemBase, isActive && "active")
                      }
                    >
                      <span className="fx-nav-icon">
                        <CalendarClock className="h-3.5 w-3.5" />
                      </span>
                      <span>{t("navigation.recurringInvoices")}</span>
                    </NavLink>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ) : (
              <>
                <NavLink
                  to="/invoices/new"
                  onClick={() => {
                    if (window.innerWidth < 1024) closeSidebar();
                  }}
                  className={({ isActive }) =>
                    cn(navItemBase, navItemCollapsed, isActive && "active")
                  }
                  title={t("navigation.newInvoice")}
                >
                  <span className="fx-nav-icon">
                    <Plus className="h-4 w-4" />
                  </span>
                </NavLink>
                <NavLink
                  to="/invoices"
                  end
                  onClick={() => {
                    if (window.innerWidth < 1024) closeSidebar();
                  }}
                  className={({ isActive }) =>
                    cn(navItemBase, navItemCollapsed, isActive && "active")
                  }
                  title={t("navigation.emitidas")}
                >
                  <span className="fx-nav-icon">
                    <FileText className="h-4 w-4" />
                  </span>
                </NavLink>
                <NavLink
                  to="/invoices/recibidas"
                  onClick={() => {
                    if (window.innerWidth < 1024) closeSidebar();
                  }}
                  className={({ isActive }) =>
                    cn(navItemBase, navItemCollapsed, isActive && "active")
                  }
                  title={t("navigation.recibidas")}
                >
                  <span className="fx-nav-icon">
                    <FileDown className="h-4 w-4" />
                  </span>
                </NavLink>
                <NavLink
                  to="/invoices/recurring"
                  onClick={() => {
                    if (window.innerWidth < 1024) closeSidebar();
                  }}
                  className={({ isActive }) =>
                    cn(navItemBase, navItemCollapsed, isActive && "active")
                  }
                  title={t("navigation.recurringInvoices")}
                >
                  <span className="fx-nav-icon">
                    <CalendarClock className="h-4 w-4" />
                  </span>
                </NavLink>
              </>
            )
          ) : null}

          {showTenantSidebar ? (
            isSidebarOpen ? (
              <Collapsible
                defaultOpen={isContabilidadSection}
                className="group/collapsible"
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      navItemBase,
                      "w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fx-accent-300)]",
                      isContabilidadSection && "active"
                    )}
                  >
                    <span className="fx-nav-icon">
                      <BookOpen className="h-4 w-4" />
                    </span>
                    <span className="flex-1 text-left">
                      {t("navigation.accounting")}
                    </span>
                    <ChevronRight className="fx-nav-chev h-3 w-3 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="mt-1 space-y-0.5 pl-7">
                    <NavLink
                      to="/fiscal-status"
                      end
                      onClick={() => {
                        if (window.innerWidth < 1024) closeSidebar();
                      }}
                      className={({ isActive }) =>
                        cn(navSubItemBase, isActive && "active")
                      }
                    >
                      <span className="fx-nav-icon">
                        <Heart className="h-3.5 w-3.5" />
                      </span>
                      <span>{t("navigation.fiscalStatus")}</span>
                    </NavLink>
                    <NavLink
                      to="/reports"
                      onClick={() => {
                        if (window.innerWidth < 1024) closeSidebar();
                      }}
                      className={({ isActive }) =>
                        cn(navSubItemBase, isActive && "active")
                      }
                    >
                      <span className="fx-nav-icon">
                        <BarChart3 className="h-3.5 w-3.5" />
                      </span>
                      <span>{t("navigation.reports")}</span>
                    </NavLink>
                    <NavLink
                      to="/expenses"
                      onClick={() => {
                        if (window.innerWidth < 1024) closeSidebar();
                      }}
                      className={({ isActive }) =>
                        cn(navSubItemBase, isActive && "active")
                      }
                    >
                      <span className="fx-nav-icon">
                        <Receipt className="h-3.5 w-3.5" />
                      </span>
                      <span>{t("navigation.expenses", "Gastos")}</span>
                    </NavLink>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ) : (
              <>
                <NavLink
                  to="/fiscal-status"
                  end
                  onClick={() => {
                    if (window.innerWidth < 1024) closeSidebar();
                  }}
                  className={({ isActive }) =>
                    cn(navItemBase, navItemCollapsed, isActive && "active")
                  }
                  title={t("navigation.fiscalStatus")}
                >
                  <span className="fx-nav-icon">
                    <Heart className="h-4 w-4" />
                  </span>
                </NavLink>
                <NavLink
                  to="/expenses"
                  onClick={() => {
                    if (window.innerWidth < 1024) closeSidebar();
                  }}
                  className={({ isActive }) =>
                    cn(navItemBase, navItemCollapsed, isActive && "active")
                  }
                  title={t("navigation.expenses", "Gastos")}
                >
                  <span className="fx-nav-icon">
                    <Receipt className="h-4 w-4" />
                  </span>
                </NavLink>
                <NavLink
                  to="/reports"
                  onClick={() => {
                    if (window.innerWidth < 1024) closeSidebar();
                  }}
                  className={({ isActive }) =>
                    cn(navItemBase, navItemCollapsed, isActive && "active")
                  }
                  title={t("navigation.reports")}
                >
                  <span className="fx-nav-icon">
                    <BarChart3 className="h-4 w-4" />
                  </span>
                </NavLink>
              </>
            )
          ) : null}

          {/* Cartera (Clientes + Proveedores) — pinned right under
              Contabilidad so the user's day-to-day cartera screens
              stay one click away. Renders in both expanded + collapsed
              modes following the same pattern as Documents below. */}
          {showTenantSidebar && canAccessClients ? (
            <>
              <NavLink
                to="/clients"
                onClick={() => {
                  if (window.innerWidth < 1024) closeSidebar();
                }}
                className={({ isActive }) =>
                  cn(navItemBase, isActive && "active", !isSidebarOpen && navItemCollapsed)
                }
                title={!isSidebarOpen ? t("navigation.clients") : undefined}
              >
                <span className="fx-nav-icon">
                  <UserCircle className="h-4 w-4" />
                </span>
                {isSidebarOpen && <span className="flex-1">{t("navigation.clients")}</span>}
              </NavLink>
              <NavLink
                to="/products"
                onClick={() => {
                  if (window.innerWidth < 1024) closeSidebar();
                }}
                className={({ isActive }) =>
                  cn(navItemBase, isActive && "active", !isSidebarOpen && navItemCollapsed)
                }
                title={!isSidebarOpen ? t("navigation.products", "Productos") : undefined}
              >
                <span className="fx-nav-icon">
                  <Package className="h-4 w-4" />
                </span>
                {isSidebarOpen && (
                  <span className="flex-1">{t("navigation.products", "Productos")}</span>
                )}
              </NavLink>
              <NavLink
                to="/suppliers"
                onClick={() => {
                  if (window.innerWidth < 1024) closeSidebar();
                }}
                className={({ isActive }) =>
                  cn(navItemBase, isActive && "active", !isSidebarOpen && navItemCollapsed)
                }
                title={!isSidebarOpen ? t("navigation.suppliers") : undefined}
              >
                <span className="fx-nav-icon">
                  <Truck className="h-4 w-4" />
                </span>
                {isSidebarOpen && <span className="flex-1">{t("navigation.suppliers")}</span>}
              </NavLink>
            </>
          ) : null}

          {showTenantSidebar ? (
            isSidebarOpen ? (
              <Collapsible
                defaultOpen={isDgiSection}
                className="group/collapsible-dgi"
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      navItemBase,
                      "w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fx-accent-300)]",
                      isDgiSection && "active"
                    )}
                  >
                    <span className="fx-nav-icon">
                      <FileSearch className="h-4 w-4" />
                    </span>
                    <span className="flex-1 text-left">
                      {t("navigation.dgiSection")}
                    </span>
                    <ChevronRight className="fx-nav-chev h-3 w-3 transition-transform duration-200 group-data-[state=open]/collapsible-dgi:rotate-90" />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="mt-1 space-y-0.5 pl-7">
                    <NavLink
                      to="/dgi-documents"
                      end
                      onClick={() => {
                        if (window.innerWidth < 1024) closeSidebar();
                      }}
                      className={({ isActive }) =>
                        cn(navSubItemBase, isActive && "active")
                      }
                    >
                      <span className="fx-nav-icon">
                        <FolderOpen className="h-3.5 w-3.5" />
                      </span>
                      <span>{t("navigation.dgiDocuments")}</span>
                    </NavLink>
                    <NavLink
                      to="/dgi/daily-reports"
                      onClick={() => {
                        if (window.innerWidth < 1024) closeSidebar();
                      }}
                      className={({ isActive }) =>
                        cn(navSubItemBase, isActive && "active")
                      }
                    >
                      <span className="fx-nav-icon">
                        <ScrollText className="h-3.5 w-3.5" />
                      </span>
                      <span>{t("navigation.dailyReports")}</span>
                    </NavLink>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ) : (
              <>
                <NavLink
                  to="/dgi-documents"
                  end
                  onClick={() => {
                    if (window.innerWidth < 1024) closeSidebar();
                  }}
                  className={({ isActive }) =>
                    cn(navItemBase, navItemCollapsed, isActive && "active")
                  }
                  title={t("navigation.dgiDocuments")}
                >
                  <span className="fx-nav-icon">
                    <FolderOpen className="h-4 w-4" />
                  </span>
                </NavLink>
                <NavLink
                  to="/dgi/daily-reports"
                  onClick={() => {
                    if (window.innerWidth < 1024) closeSidebar();
                  }}
                  className={({ isActive }) =>
                    cn(navItemBase, navItemCollapsed, isActive && "active")
                  }
                  title={t("navigation.dailyReports")}
                >
                  <span className="fx-nav-icon">
                    <ScrollText className="h-4 w-4" />
                  </span>
                </NavLink>
              </>
            )
          ) : null}

          {showTenantSidebar ? (
            <NavLink
              to="/agenda"
              end
              onClick={() => {
                if (window.innerWidth < 1024) closeSidebar();
              }}
              className={({ isActive }) =>
                cn(navItemBase, isActive && "active", !isSidebarOpen && navItemCollapsed)
              }
              title={!isSidebarOpen ? t("navigation.agenda") : undefined}
            >
              <span className="fx-nav-icon">
                <CalendarDays className="h-4 w-4" />
              </span>
              {isSidebarOpen && <span className="flex-1">{t("navigation.agenda")}</span>}
            </NavLink>
          ) : null}

          {(showTenantSidebar || (isPlatform && !activeTenant)) &&
            navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => {
                    if (window.innerWidth < 1024) closeSidebar();
                  }}
                  className={({ isActive }) =>
                    cn(navItemBase, isActive && "active", !isSidebarOpen && navItemCollapsed)
                  }
                  title={!isSidebarOpen ? t(item.labelKey) : undefined}
                >
                  <span className="fx-nav-icon">
                    <Icon className="h-4 w-4" />
                  </span>
                  {isSidebarOpen && <span className="flex-1">{t(item.labelKey)}</span>}
                </NavLink>
              );
            })}
          </SidebarMoreGroup>
        </nav>
        <div className="mt-auto pt-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  "fx-sidebar-user",
                  !isSidebarOpen && "lg:justify-center"
                )}
              >
                <Avatar className="avatar-wrap h-8 w-8 border-0">
                  {user?.profileImage ? (
                    <AvatarImage src={user.profileImage} alt={displayName} />
                  ) : null}
                  <AvatarFallback className="bg-[var(--fx-accent-500)] text-[var(--fx-paper)]">
                    {getInitials(user?.email, user?.firstName, user?.lastName)}
                  </AvatarFallback>
                </Avatar>
                {isSidebarOpen && (
                  <div className="info flex min-w-0 flex-1 flex-col items-start overflow-hidden">
                    <span className="name">
                      {displayName || t("header.unknown")}
                    </span>
                    {user?.email && (
                      <span className="mail">{user.email}</span>
                    )}
                  </div>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align={isSidebarOpen ? "start" : "end"}
              side="top"
              className="w-56"
            >
              <DropdownMenuLabel>{t("header.account")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {!isImpersonating && (
                <DropdownMenuItem
                  onClick={() =>
                    navigate(
                      isAccountant && onAccountantHome
                        ? "/accountant/profile"
                        : "/profile",
                    )
                  }
                >
                  <span className="flex size-4 shrink-0 items-center justify-center">
                    <UserCircle className="size-4" aria-hidden />
                  </span>
                  {t("header.profile")}
                </DropdownMenuItem>
              )}
              <ThemePreferenceSubmenu />
              <DropdownMenuItem onClick={() => setFeedbackOpen(true)}>
                <span className="flex size-4 shrink-0 items-center justify-center">
                  <MessageCircle className="size-4" aria-hidden />
                </span>
                {t("header.feedback")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout}>
                <span className="flex size-4 shrink-0 items-center justify-center">
                  <LogOut className="size-4" aria-hidden />
                </span>
                {t("header.signOut")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
      <NuevaFacturaDialog open={pickerOpen} onOpenChange={setPickerOpen} />
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      <ConfirmationDialog
        open={isLogoutDialogOpen}
        onOpenChange={setIsLogoutDialogOpen}
        title={t("header.logoutDialog.title")}
        description={t("header.logoutDialog.description")}
        confirmText={t("header.logoutDialog.confirm")}
        cancelText={t("header.logoutDialog.cancel")}
        variant="destructive"
        onConfirm={handleLogoutConfirm}
      />
    </>
  );
}
